// =============================================================================
// RepeatCheckSettings.jsx — System Setup → Access: the possible-repeat flag settings
// =============================================================================
// Possible-repeat flag (db-210). Two portal_config settings, no migration:
//   • repeat_check_months — how far back to look (whole months, default 12)
//   • repeat_check_types  — which item types are checked (array; absent = all)
// Uniforms and shoes are never checked (David, 21 Oct 2026). The matching rules live in
// the database function request_repeat_matches(); this screen only sets its inputs.
// Responders pick up a change the next time the portal loads.
// =============================================================================
import React from "react";
import { getConfigValue, upsertConfig } from "../../data/portalConfig";
import {
  normaliseRepeatMonths,
  REPEAT_CHECK_ALL_TYPES,
  REPEAT_CHECK_DEVICE,
  REPEAT_MONTHS_KEY,
  REPEAT_TYPES_KEY,
} from "../../services/repeatCheck";
import { logAudit } from "../../utils/auditLog";
import { feeTypeLabel } from "../../utils/formConstants";
import { Btn } from "../../utils/ui.jsx";

function typesFromStored(v) {
  return Array.isArray(v)
    ? REPEAT_CHECK_ALL_TYPES.filter((t) => v.includes(t))
    : [...REPEAT_CHECK_ALL_TYPES];
}

export function RepeatCheckSettings() {
  const [months, setMonths] = React.useState("12");
  const [types, setTypes] = React.useState([...REPEAT_CHECK_ALL_TYPES]);
  const [saving, setSaving] = React.useState(false);
  const [msg, setMsg] = React.useState("");

  React.useEffect(function () {
    let alive = true;
    Promise.all([getConfigValue(REPEAT_MONTHS_KEY), getConfigValue(REPEAT_TYPES_KEY)]).then(
      function ([m, t]) {
        if (!alive) return;
        setMonths(String(normaliseRepeatMonths(m)));
        setTypes(typesFromStored(t));
      }
    );
    return function () {
      alive = false;
    };
  }, []);

  const monthsNum = Number(months);
  const monthsValid = Number.isInteger(monthsNum) && monthsNum >= 1 && monthsNum <= 60;

  function toggle(t) {
    setTypes(function (cur) {
      return cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t];
    });
  }

  async function save() {
    if (!monthsValid) return;
    setSaving(true);
    const ordered = REPEAT_CHECK_ALL_TYPES.filter((t) => types.includes(t));
    const [r1, r2] = await Promise.all([
      upsertConfig(REPEAT_MONTHS_KEY, monthsNum),
      upsertConfig(REPEAT_TYPES_KEY, ordered),
    ]);
    setSaving(false);
    const err = r1.error || r2.error;
    if (err) {
      setMsg(`Save failed: ${err.message}`);
      return;
    }
    logAudit("repeat_check_settings_saved", { months: monthsNum, types: ordered });
    setMsg("Saved ✓ — responders pick this up the next time the portal loads.");
    setTimeout(function () {
      setMsg("");
    }, 5000);
  }

  return (
    <section
      aria-label="Possible repeat check"
      style={{ marginTop: 24, paddingTop: 16, borderTop: "1px solid #CFE0DE" }}
    >
      <h3 style={{ margin: "0 0 6px", fontSize: 15, color: "#00695C" }}>Possible repeat check</h3>
      <p className="m-0 mb-3 text-[13px] text-zephyr-muted">
        Flags a fee or device that looks like one already requested for the same child (same DFV
        Service, gender, year level and school) and asks the responder for a Repeat check note at
        Confirm &amp; Send. Uniforms and shoes are never checked.
      </p>
      <label
        htmlFor="repeat-check-months"
        style={{ display: "block", fontSize: 13, fontWeight: 700, marginBottom: 4 }}
      >
        Look back (months)
      </label>
      <input
        id="repeat-check-months"
        type="number"
        min={1}
        max={60}
        value={months}
        onChange={function (e) {
          setMonths(e.target.value);
        }}
        style={{
          width: 90,
          padding: "6px 8px",
          fontSize: 14,
          border: `1px solid ${monthsValid ? "#CFE0DE" : "#C62828"}`,
          borderRadius: 6,
          fontFamily: "inherit",
        }}
      />
      {!monthsValid && (
        <span style={{ marginLeft: 8, fontSize: 12, color: "#C62828" }}>
          Enter a whole number from 1 to 60
        </span>
      )}
      <fieldset style={{ border: "none", padding: 0, margin: "14px 0 0" }}>
        <legend style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Items to check</legend>
        <div
          style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 260px))", gap: 6 }}
        >
          {REPEAT_CHECK_ALL_TYPES.map(function (t) {
            const id = `repeat-type-${t.replace(/[^a-z0-9]+/gi, "-")}`;
            return (
              <label key={t} htmlFor={id} style={{ fontSize: 13, display: "flex", gap: 6 }}>
                <input
                  id={id}
                  type="checkbox"
                  checked={types.includes(t)}
                  onChange={function () {
                    toggle(t);
                  }}
                />
                {t === REPEAT_CHECK_DEVICE ? "Devices" : feeTypeLabel(t)}
              </label>
            );
          })}
        </div>
      </fieldset>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 14 }}>
        <Btn onClick={save} disabled={saving || !monthsValid}>
          {saving ? "Saving…" : "Save repeat check settings"}
        </Btn>
        {msg && (
          <span
            role="status"
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: msg.startsWith("Save failed") ? "#C62828" : "#2E7D32",
            }}
          >
            {msg}
          </span>
        )}
      </div>
    </section>
  );
}
